/**
 * useHandoverTransition — the Awaiting Signature → Signed & Sent to New
 * Business handover flow of File Detail (decomposition step F3, CLAUDE.md
 * §8.4; the flow itself is §35 and the handover-notes lock rule).
 *
 * Owns the modal state (`open`, `busy`, `error`) and the two handlers the
 * modal calls. `start` opens the modal, `cancel` closes it, and `confirm`
 * runs the handover in the ONE order that matters (audit finding 1.2,
 * fixed in PR #63): fetch the notepad, generate and download the memo PDF,
 * and only then write the status together with the notes lock. A failure
 * before the write saves and locks nothing and leaves the modal open with
 * its error so the notes stay editable; a failure at the write leaves the
 * modal open too and never returns to the dashboard.
 *
 * The hook does not navigate. On success it closes the modal and calls
 * `onSuccess(updated)`; the view applies its own UI follow-through and the
 * dashboard return (§39) there. The bodies of `confirm` and `cancel` are
 * FileDetail's former handleConfirmSignedTransition and
 * handleCancelSignedTransition, moved verbatim apart from the closure
 * substitutions (the view's state setters → this hook's, the success tail →
 * `onSuccess`).
 *
 * Inputs: `file` (the loaded record), `id` (the route id), `user` and
 * `role` (from useAuth in the view), `onSuccess(updated)`.
 */

import { useState } from 'react';
import { getNotesForFile } from '../api/fileNotesApi';
import { getFirstName } from '../auth/authConfig';
import { markSigned } from '../services/pipelineService';
import { toMemoNotepadEntry } from '../utils/handoverMemoNotes';
import { defaultCommissionSplits } from '../components/files/CommissionSplitEditor';

export default function useHandoverTransition({ file, id, user, role, onSuccess }) {
  // Awaiting Signature → Signed & sent to NB transition modal.
  // The modal is opened from the action button, generates the handover
  // memo PDF, and only flips the status when generation succeeds.
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Opens the modal (the former trigger onClick in the Actions card).
  const start = () => {
    setError(null);
    setOpen(true);
  };

  // Awaiting Signature → Signed & Sent to NB transition.
  // Order of operations (the lock is committed LAST, atomically with the
  // status change — a failure anywhere before that writes nothing):
  //   1. Fetch the file's notepad history (oldest first) for page 2+.
  //   2. Generate the memo PDF from the notes as typed in the modal and
  //      trigger the download. Nothing has been written yet, so if this
  //      throws the notes stay unsaved and fully editable, the file stays
  //      in Awaiting Signature, and the user can correct and retry.
  //   3. Only after the PDF succeeded: ONE write via
  //      pipelineService.markSigned that flips the status AND saves +
  //      locks the "Notes for New Business" (files.handover_notes /
  //      handover_notes_locked_at, migration 27) together. If the notes
  //      were already locked (a legacy record) the stored value is reused
  //      and only the status is written.
  //   If the write in step 3 fails the memo has already downloaded but
  //   nothing was saved or locked; the modal stays open with an error
  //   that says so, and a retry regenerates the memo and tries again.
  //   There is no storage upload and no memo record (the memo is
  //   download-only, CLAUDE.md §35.1), so a failed attempt leaves no
  //   server-side artefact to clean up.
  // The handover notes are NOT a file note: no note row, no card
  // illumination, no acknowledgement routing, no stat input.
  // `adviserNotes` is the modal's editable copy of the file's Summary
  // (files.written_instructions) — it shapes the memo's Notes card only and
  // is NEVER written back to the file record.
  const confirm = async ({ adviserNotes, handoverNotes, dateSigned, dateSubmittedToNb }) => {
    if (busy) return;
    setError(null);
    setBusy(true);

    const fromName = getFirstName(user?.email);
    const fromRole = role === 'assistant' ? 'Administration Assistant' : 'Adviser';
    const actor = { email: user?.email, id: user?.id, firstName: fromName, role };
    // Each case carries a free-text description (case_labels[i]) —
    // the adviser fills it in comprehensively at submission time and
    // the handover memo reads it back verbatim. We pass the trimmed
    // descriptions straight through; line breaks the adviser typed
    // are preserved by both the modal and the PDF generator.
    const caseDescriptions = (Array.isArray(file.caseLabels) ? file.caseLabels : [])
      .map((s) => (typeof s === 'string' ? s : ''));

    // Notes printed on the memo. Already locked → reuse the stored value.
    // Not yet locked → the Administration Assistant's typed text (other
    // roles cannot type here, so they lock an empty value, as before).
    const alreadyLocked = Boolean(file.handoverNotesLockedAt);
    const memoNotes = alreadyLocked
      ? (file.handoverNotes || '').trim()
      : (role === 'assistant' ? (handoverNotes || '').trim() : '');

    // Steps 1–2: build and download the memo. No database writes here.
    try {
      const notepadNotes = (await getNotesForFile(id)).map(toMemoNotepadEntry);

      // Lazy-load the PDF generator so jsPDF isn't pulled into the
      // main bundle. The file detail page renders fine without it; we
      // only need it on this confirm action.
      const { generateHandoverMemoPdf } = await import('../utils/handoverMemoPdf');

      await generateHandoverMemoPdf({
        file,
        clientName: file.clientName,
        dateSigned,
        dateSubmittedToNb,
        commissionSplits: file.commissionSplits || defaultCommissionSplits(),
        cases: caseDescriptions,
        adviserNotes: (adviserNotes || '').trim(),
        handoverNotes: memoNotes,
        notepadNotes,
        fromName,
        fromRole,
        priority: file.priority,
      });
    } catch (err) {
      console.error('Handover memo generation failed:', err);
      setError(
        err && err.message
          ? `Failed to generate the handover memo PDF: ${err.message.replace(/\.$/, '')}. Nothing was saved — you can correct the notes and try again.`
          : 'Failed to generate the handover memo PDF. Nothing was saved — you can correct the notes and try again.'
      );
      setBusy(false);
      return;
    }

    // Step 3: the PDF exists. Commit status + notes lock in ONE write,
    // through the pipeline service chokepoint (CLAUDE.md §7.6) so the
    // system note and signed_at timestamp are written as for any
    // transition. Called directly (not via handleStatusChange) so a
    // failure reaches this modal instead of being swallowed.
    let updated;
    try {
      updated = await markSigned(file, actor, {
        handoverLock: alreadyLocked
          ? null
          : { notes: memoNotes || null, lockedAt: new Date().toISOString() },
      });
    } catch (err) {
      console.error('Signed & Sent to NB status update failed:', err);
      setError(
        'The handover memo PDF downloaded, but the file could not be moved to Signed & Sent to New Business'
          + (err && err.message ? `: ${err.message.replace(/\.$/, '')}.` : '.')
          + ' Nothing was saved or locked. Delete the downloaded copy, then try again.'
      );
      setBusy(false);
      return;
    }

    // Success — close the modal, then hand the updated record to the view,
    // which applies its UI follow-through (setFile, notes refresh, status
    // animation, files refetch) and, last, the dashboard return (§39).
    // Either failure path above returned early, leaving the modal open
    // with its error, so onSuccess is reached only after the memo PDF has
    // been generated AND downloaded (steps 1–2) and the status write has
    // succeeded (step 3).
    setOpen(false);
    setBusy(false);
    if (typeof onSuccess === 'function') await onSuccess(updated);
  };

  const cancel = () => {
    if (busy) return;
    setOpen(false);
    setError(null);
  };

  return { open, busy, error, start, cancel, confirm };
}
